// =============================================================================
// src/services/unleashedCustomerSync.ts
// -----------------------------------------------------------------------------
// PURE compare/plan for the Customer sync (Function 2 — the 5th manual sync
// utility). Given the slim customers fetched from Unleashed (GET /Customers) and
// our `shelters` rows, it classifies each shelter into link / create / update /
// in-sync and (via the declared authority) sets the default tick state of each
// row. Writes nothing — the Sync Utilities screen applies the ticked rows through
// the push-customers Edge Function.
//
// Buckets (matched on CustomerCode = shelter_code):
//   • link    — shelter has NO guid, but a matching Unleashed customer exists →
//               record its GUID (no POST). This is the one-off backfill of the
//               ~266 imported shelters. `hasUnlinked` gates create-push OFF until
//               the link bucket is empty (so we can never duplicate the ~266).
//   • create  — no guid, no Unleashed match → POST create.
//   • update  — guid present, mapped fields differ → POST update.
//   • inSync  — guid present, no diff (or a deactivated shelter that has a guid).
// A deactivated shelter can LINK (harmless) but is never created/updated.
//
// Update rows default to TICKED: the portal is the source of truth for the fields
// it pushes (the Source-of-Truth toggle was retired 3 Aug 2026 — auto-sync makes the
// portal authoritative). link + create rows are always default-ticked too.
//
// A change's { from, to } reads "Unleashed has FROM; applying will push our value
// TO" (from = Unleashed's current value, to = the portal value we would send).
// =============================================================================

import {
  normalizeAddressKey,
  type ShippingAddressInput,
  type SlimCustomer,
} from "../../supabase/functions/_shared/unleashed/customerMap";
import type { ShelterRow } from "../data/shelters";

export interface CustomerChange {
  field:
    | "name"
    | "customerType"
    | "salesGroup"
    | "taxable"
    | "stopCredit"
    | "abn"
    | "bankName"
    | "bankAccount"
    | "defaultWarehouse"
    | "physicalAddress"
    | "postalAddress"
    | "deliveryAddresses";
  from: string | null; // Unleashed's current value
  to: string | null; // the portal value we would push
}

// Bank details live in the admin-only shelter_bank_details table (not on ShelterRow), so
// the caller passes them in as a shelter_id → {bank_name, bank_account} lookup.
export interface ShelterBank {
  bank_name: string | null;
  bank_account: string | null;
}

export interface CustomerLink {
  shelterId: string;
  shelterCode: string;
  shelterName: string;
  guid: string; // the GUID to record on shelters.unleashed_guid
  defaultSelected: true;
}

export interface CustomerCreate {
  shelterId: string;
  shelterCode: string;
  shelterName: string;
  defaultSelected: true;
}

export interface CustomerUpdate {
  shelterId: string;
  shelterCode: string;
  shelterName: string;
  guid: string;
  changes: CustomerChange[];
  defaultSelected: boolean; // authority-driven
}

// WHY a service is an anomaly. Both mean "don't touch it", but they point at
// different upstream problems.
export type CustomerUnmatchedReason =
  | "match_without_guid" // the code exists in Unleashed but the row carried no Guid
  | "linked_code_missing"; // portal holds a GUID, but Unleashed no longer returns that code

export interface UnmatchedCustomerRow {
  shelterId: string;
  shelterCode: string;
  shelterName: string | null;
  reason: CustomerUnmatchedReason;
}

export interface CustomerSyncPlan {
  toLink: CustomerLink[];
  toCreate: CustomerCreate[];
  toUpdate: CustomerUpdate[];
  inSyncCount: number;
  skippedDeactivated: number; // deactivated, no guid, no Unleashed match → not created
  // The anomalies, WITH the reason — a bare count can't be acted on.
  unmatched: UnmatchedCustomerRow[];
  unmatchedCount: number; // === unmatched.length (kept for existing callers)
  hasUnlinked: boolean; // toLink non-empty → create-push must stay interlocked
}

const norm = (v: string | null | undefined): string | null => {
  const s = (v ?? "").trim();
  return s === "" ? null : s;
};
const boolStr = (v: boolean | null | undefined): string | null =>
  v === null || v === undefined ? null : v ? "true" : "false";

// Which mapped fields differ between our shelter and the Unleashed customer.
function diffCustomer(
  shelter: ShelterRow,
  inc: SlimCustomer,
  bank?: ShelterBank,
  delivery?: ShippingAddressInput[]
): CustomerChange[] {
  const changes: CustomerChange[] = [];
  if (norm(shelter.shelter_name) !== norm(inc.customerName)) {
    changes.push({ field: "name", from: norm(inc.customerName), to: norm(shelter.shelter_name) });
  }
  if (norm(shelter.customer_type) !== norm(inc.customerType)) {
    changes.push({
      field: "customerType",
      from: norm(inc.customerType),
      to: norm(shelter.customer_type),
    });
  }
  if (norm(shelter.sales_group) !== norm(inc.salesGroup)) {
    changes.push({
      field: "salesGroup",
      from: norm(inc.salesGroup),
      to: norm(shelter.sales_group),
    });
  }
  if (boolStr(shelter.taxable) !== boolStr(inc.taxable)) {
    changes.push({ field: "taxable", from: boolStr(inc.taxable), to: boolStr(shelter.taxable) });
  }
  if (boolStr(shelter.stop_credit) !== boolStr(inc.stopCredit)) {
    changes.push({
      field: "stopCredit",
      from: boolStr(inc.stopCredit),
      to: boolStr(shelter.stop_credit),
    });
  }
  // ABN is portal-owned and pushed. Flag a change ONLY when the portal HOLDS a value that
  // differs — mirroring the push (sends the portal value when present, carries Unleashed's
  // when empty). So an empty portal field never shows a spurious "update". (Notes is NOT
  // diffed: it's carried live / read-only — the portal never pushes or overwrites it.)
  if (norm(shelter.abn) && norm(shelter.abn) !== norm(inc.gstVatNumber)) {
    changes.push({ field: "abn", from: norm(inc.gstVatNumber), to: norm(shelter.abn) });
  }
  // Default warehouse (Unleashed CODE, e.g. "W1"). Portal-owned; flag when the portal
  // holds one that differs (empty portal → push carries Unleashed's, so no diff).
  if (
    norm(shelter.default_warehouse) &&
    norm(shelter.default_warehouse) !== norm(inc.defaultWarehouse)
  ) {
    changes.push({
      field: "defaultWarehouse",
      from: norm(inc.defaultWarehouse),
      to: norm(shelter.default_warehouse),
    });
  }
  // Bank Name / Account — from the admin-only shelter_bank_details lookup (passed in),
  // same portal-wins-when-present rule as Notes/ABN/warehouse.
  if (bank && norm(bank.bank_name) && norm(bank.bank_name) !== norm(inc.bankName)) {
    changes.push({ field: "bankName", from: norm(inc.bankName), to: norm(bank.bank_name) });
  }
  if (bank && norm(bank.bank_account) && norm(bank.bank_account) !== norm(inc.bankAccount)) {
    changes.push({
      field: "bankAccount",
      from: norm(inc.bankAccount),
      to: norm(bank.bank_account),
    });
  }
  // Physical / Postal address (portal-owned). Normalize both sides identically (locality
  // → Town/City, same as the push) and flag when the portal HOLDS an address that differs.
  const portalPhysical = normalizeAddressKey(
    shelter.physical_line1,
    shelter.physical_line2,
    shelter.physical_suburb_city,
    shelter.physical_state,
    shelter.physical_postcode
  );
  if (portalPhysical && portalPhysical !== inc.physicalAddr) {
    changes.push({ field: "physicalAddress", from: inc.physicalAddr, to: portalPhysical });
  }
  const portalPostal = normalizeAddressKey(
    shelter.postal_line1,
    shelter.postal_line2,
    shelter.postal_suburb_city,
    shelter.postal_state,
    shelter.postal_postcode
  );
  if (portalPostal && portalPostal !== inc.postalAddr) {
    changes.push({ field: "postalAddress", from: inc.postalAddr, to: portalPostal });
  }
  // Delivery (Shipping) addresses — portal-owned. Flag only when the portal holds an
  // address that is NOT yet in Unleashed's Shipping set (a SUBSET check, not strict
  // equality). Why subset: a Customer POST can only ADD/UPDATE Shipping entries — it
  // cannot DELETE, and Unleashed keeps any Shipping the portal doesn't have (e.g. a
  // legacy address entered directly in Unleashed). Strict equality would flag such a
  // customer forever with no way to reconcile; the subset check reads in-sync once every
  // portal address exists in Unleashed. Empty portal → nothing to push (the existing
  // Unleashed Shipping is carried). CONSEQUENCE: a delivery address REMOVED in the portal
  // is not deleted from Unleashed — the Customer push has no delete path (see the plan doc).
  const portalShipping = (delivery ?? [])
    .map((d) => normalizeAddressKey(d.line1, d.line2, d.suburb_city, d.state, d.postcode))
    .filter((k): k is string => k !== null)
    .sort();
  if (portalShipping.length > 0) {
    const unleashedSet = new Set(inc.shippingAddrs ?? []);
    const missing = portalShipping.filter((k) => !unleashedSet.has(k));
    if (missing.length > 0) {
      const unleashedShipping = [...(inc.shippingAddrs ?? [])].sort();
      changes.push({
        field: "deliveryAddresses",
        from: unleashedShipping.length ? unleashedShipping.join(" · ") : null,
        to: portalShipping.join(" · "),
      });
    }
  }
  return changes;
}

export function planCustomerSync(
  incoming: SlimCustomer[],
  shelters: ShelterRow[],
  bankByShelterId?: Map<string, ShelterBank>,
  deliveryByShelterId?: Map<string, ShippingAddressInput[]>
): CustomerSyncPlan {
  const byCode = new Map<string, SlimCustomer>();
  for (const c of incoming) {
    const code = (c.customerCode ?? "").trim();
    if (code) byCode.set(code, c);
  }

  const plan: CustomerSyncPlan = {
    toLink: [],
    toCreate: [],
    toUpdate: [],
    inSyncCount: 0,
    skippedDeactivated: 0,
    unmatched: [],
    unmatchedCount: 0,
    hasUnlinked: false,
  };

  for (const s of shelters) {
    const code = (s.shelter_code ?? "").trim();
    if (!code) continue;
    const inc = byCode.get(code);
    const deactivated = s.deactivated === true;

    if (!s.unleashed_guid) {
      // No GUID yet.
      if (inc) {
        if (inc.guid) {
          // Exists in Unleashed → LINK (record GUID). Allowed even if deactivated.
          plan.toLink.push({
            shelterId: s.id,
            shelterCode: code,
            shelterName: s.shelter_name,
            guid: inc.guid,
            defaultSelected: true,
          });
        } else {
          // Matched by code but no Guid on the row — can't link, must not create
          // (the code exists in Unleashed). Anomaly; report only.
          plan.unmatched.push({
            shelterId: s.id,
            shelterCode: code,
            shelterName: s.shelter_name,
            reason: "match_without_guid",
          });
        }
      } else {
        // Not in Unleashed → CREATE (never create a deactivated service).
        if (deactivated) {
          plan.skippedDeactivated += 1;
        } else {
          plan.toCreate.push({
            shelterId: s.id,
            shelterCode: code,
            shelterName: s.shelter_name,
            defaultSelected: true,
          });
        }
      }
      continue;
    }

    // Has a GUID.
    if (!inc) {
      // Linked before but Unleashed no longer returns this code (renamed/removed
      // upstream). Anomaly; report only — never auto-create a duplicate.
      plan.unmatched.push({
        shelterId: s.id,
        shelterCode: code,
        shelterName: s.shelter_name,
        reason: "linked_code_missing",
      });
      continue;
    }
    if (deactivated) {
      // Don't push changes to a deactivated service; count as in-sync (nothing to do).
      plan.inSyncCount += 1;
      continue;
    }
    const changes = diffCustomer(
      s,
      inc,
      bankByShelterId?.get(s.id),
      deliveryByShelterId?.get(s.id)
    );
    if (changes.length === 0) {
      plan.inSyncCount += 1;
      continue;
    }
    plan.toUpdate.push({
      shelterId: s.id,
      shelterCode: code,
      shelterName: s.shelter_name,
      guid: s.unleashed_guid,
      changes,
      defaultSelected: true, // portal is the source of truth for the pushed fields
    });
  }

  plan.hasUnlinked = plan.toLink.length > 0;
  plan.unmatchedCount = plan.unmatched.length;
  return plan;
}
